import { useMemo } from "react";
import { useUserContext } from "../../Contexts/UserProvider";

/**
 * Custom hook to check if the current user has specific permissions.
 * 
 * @param requiredKeys - A single permission key or an array of keys.
 * @param mode - 'all' requires all keys to be present; 'any' requires at least one.
 * @returns boolean indicating if the user is authorized.
 */
export const useHasPermission = (
  requiredKeys: string | string[],
  mode: 'all' | 'any' = 'any'
): boolean => {
  const { user } = useUserContext();
  const userPermissions = user?.permissions || [];

  return useMemo(() => {
    const required = Array.isArray(requiredKeys) ? requiredKeys : [requiredKeys];

    if (required.length === 0) return true;

    if (mode === "all") {
      return required.every((key) => userPermissions.includes(key));
    }

    // Default: 'any'
    return required.some((key) => userPermissions.includes(key));
  }, [userPermissions, requiredKeys, mode]);
};
